/**
 * Style5 (Spotlight) — Style-specific sections for GridEngine.
 *
 * live_draw: Theater-themed 2-col layout with TheaterJackpotCard + 50/50 split
 * how_it_works: 4-step grid with emoji icons and charity-themed backgrounds
 * final_cta: Dark-themed CTA with trophy emoji and charity-colored button
 */
import React, { useState, useEffect } from 'react'
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import { useContent } from '../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../hooks/usePurchaseNavigate'

// Theater-style Jackpot Card with Chase Lights (extracted from Classic5050Style5.jsx)
function TheaterJackpotCard({ jackpot, ticketsSold, timeRemaining, t, editMode, handleTranslationUpdate, c }) {
  const [lightIndex, setLightIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setLightIndex((prev) => (prev + 1) % 24)
    }, 80)
    return () => clearInterval(interval)
  }, [])

  const winnerHalf = Math.floor((jackpot || 0) / 2)
  const charityHalf = (jackpot || 0) - winnerHalf

  const ChaseLights = ({ count = 24 }) => {
    const lights = []
    const perSide = Math.floor(count / 4)

    // Top edge
    for (let i = 0; i < perSide; i++) {
      const isActive = Math.abs(i - (lightIndex % count)) <= 1 || Math.abs(i - (lightIndex % count)) >= count - 1
      lights.push(
        <div key={`top-${i}`} className="absolute w-3 h-3 rounded-full transition-all duration-150" style={{
          top: '-6px', left: `${(i / perSide) * 100}%`,
          backgroundColor: isActive ? 'var(--charity-primary)' : 'color-mix(in srgb, var(--charity-primary) 30%, transparent)',
          boxShadow: isActive ? '0 0 10px 3px color-mix(in srgb, var(--charity-primary) 70%, transparent)' : 'none',
        }} />
      )
    }
    // Right edge
    for (let i = 0; i < perSide; i++) {
      const idx = perSide + i
      const isActive = Math.abs(idx - (lightIndex % count)) <= 1 || Math.abs(idx - (lightIndex % count)) >= count - 1
      lights.push(
        <div key={`right-${i}`} className="absolute w-3 h-3 rounded-full transition-all duration-150" style={{
          right: '-6px', top: `${(i / perSide) * 100}%`,
          backgroundColor: isActive ? 'var(--charity-primary)' : 'color-mix(in srgb, var(--charity-primary) 30%, transparent)',
          boxShadow: isActive ? '0 0 10px 3px color-mix(in srgb, var(--charity-primary) 70%, transparent)' : 'none',
        }} />
      )
    }
    // Bottom edge
    for (let i = 0; i < perSide; i++) {
      const idx = perSide * 2 + i
      const isActive = Math.abs(idx - (lightIndex % count)) <= 1 || Math.abs(idx - (lightIndex % count)) >= count - 1
      lights.push(
        <div key={`bottom-${i}`} className="absolute w-3 h-3 rounded-full transition-all duration-150" style={{
          bottom: '-6px', right: `${(i / perSide) * 100}%`,
          backgroundColor: isActive ? 'var(--charity-primary)' : 'color-mix(in srgb, var(--charity-primary) 30%, transparent)',
          boxShadow: isActive ? '0 0 10px 3px color-mix(in srgb, var(--charity-primary) 70%, transparent)' : 'none',
        }} />
      )
    }
    // Left edge
    for (let i = 0; i < perSide; i++) {
      const idx = perSide * 3 + i
      const isActive = Math.abs(idx - (lightIndex % count)) <= 1 || Math.abs(idx - (lightIndex % count)) >= count - 1
      lights.push(
        <div key={`left-${i}`} className="absolute w-3 h-3 rounded-full transition-all duration-150" style={{
          left: '-6px', bottom: `${(i / perSide) * 100}%`,
          backgroundColor: isActive ? 'var(--charity-primary)' : 'color-mix(in srgb, var(--charity-primary) 30%, transparent)',
          boxShadow: isActive ? '0 0 10px 3px color-mix(in srgb, var(--charity-primary) 70%, transparent)' : 'none',
        }} />
      )
    }

    return <>{lights}</>
  }

  return (
    <div className="relative">
      <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4" style={{
        backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
        borderColor: 'color-mix(in srgb, var(--charity-primary) 55%, transparent)',
      }}>
        <div className="absolute inset-0 pointer-events-none"><ChaseLights count={24} /></div>

        {/* Trim bars */}
        <div className="relative p-6 md:p-8">
          <div className="absolute top-2 left-4 right-4 h-[2px] rounded-full" style={{
            background: `linear-gradient(to right, color-mix(in srgb, var(--charity-primary) 60%, transparent), var(--charity-primary), color-mix(in srgb, var(--charity-primary) 60%, transparent))`,
          }} />
          <div className="absolute bottom-2 left-4 right-4 h-[2px] rounded-full" style={{
            background: `linear-gradient(to right, color-mix(in srgb, var(--charity-primary) 60%, transparent), var(--charity-primary), color-mix(in srgb, var(--charity-primary) 60%, transparent))`,
          }} />

          <div className="text-center py-4">
            <div className="inline-block px-6 py-2 rounded-full mb-4" style={{ borderColor: 'color-mix(in srgb, var(--charity-primary) 30%, transparent)', borderWidth: '1px' }}>
              <span className="text-xs uppercase tracking-[0.3em] font-bold" style={{ color: 'var(--charity-primary-contrast)' }}>
                <InlineTranslationEdit translationKey="classic5050.currentJackpot" value={c('classic5050.currentJackpot', t?.('classic5050.currentJackpot'))} fallback="Current Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} />
              </span>
            </div>

            <div className="relative inline-block" style={{ color: 'var(--charity-primary-contrast)' }}>
              <Sparkles className="absolute -top-2 -right-6 w-6 h-6 animate-pulse" style={{ color: 'var(--charity-primary-contrast)' }} />
              <Sparkles className="absolute -bottom-2 -left-6 w-5 h-5 animate-pulse" style={{ color: 'var(--charity-primary-contrast)', animationDelay: '0.5s' }} />
              <div className="flex items-start gap-1">
                <span className="text-3xl font-black mt-3" style={{ color: 'var(--charity-primary-contrast)' }}>$</span>
                <span className="text-6xl md:text-7xl lg:text-8xl font-black tracking-tight" style={{
                  color: 'var(--charity-primary-contrast)',
                  textShadow: '0 0 40px color-mix(in srgb, var(--charity-primary-contrast) 60%, transparent)',
                }}>
                  {(jackpot || 0).toLocaleString()}
                </span>
              </div>
            </div>

            {/* 50/50 split labels */}
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-100 dark:text-emerald-200">${winnerHalf.toLocaleString()}</p>
                <p className="text-emerald-200/80 dark:text-emerald-300/70 text-xs mt-1">
                  <InlineTranslationEdit translationKey="classic5050.winnerShare" value={c('classic5050.winnerShare', t?.('classic5050.winnerShare'))} fallback="Winner" onUpdate={handleTranslationUpdate} editMode={editMode} />
                </p>
              </div>
              <div className="text-white/40 text-2xl">|</div>
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-100 dark:text-emerald-200">${charityHalf.toLocaleString()}</p>
                <p className="text-emerald-200/80 dark:text-emerald-300/70 text-xs mt-1">
                  <InlineTranslationEdit translationKey="classic5050.charityShare" value={c('classic5050.charityShare', t?.('classic5050.charityShare'))} fallback="Charity" onUpdate={handleTranslationUpdate} editMode={editMode} />
                </p>
              </div>
            </div>

            {/* Ticket count + countdown */}
            {ticketsSold != null && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <Ticket className="w-4 h-4" style={{ color: 'var(--charity-primary-contrast)', opacity: 0.8 }} />
                <span className="text-sm" style={{ color: 'var(--charity-primary-contrast)', opacity: 0.9 }}>
                  {c('classic5050.ticketsSoldCount', t?.('classic5050.ticketsSoldCount', '{count} tickets sold'))
                    .replace('{count}', ticketsSold.toLocaleString())}
                </span>
              </div>
            )}

            {/* Countdown */}
            <div className="grid grid-cols-4 gap-3 mt-6 max-w-xs mx-auto">
              {[
                { value: timeRemaining.days, label: 'Days', tKey: 'classic5050.days' },
                { value: timeRemaining.hours, label: 'Hrs', tKey: 'classic5050.hours' },
                { value: timeRemaining.minutes, label: 'Min', tKey: 'classic5050.mins' },
                { value: timeRemaining.seconds, label: 'Sec', tKey: 'classic5050.secs' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="bg-black/40 dark:bg-black/50 rounded-lg p-2 backdrop-blur-sm" style={{ borderColor: 'color-mix(in srgb, var(--charity-primary) 50%, transparent)', borderWidth: '1px' }}>
                    <div className="text-2xl md:text-3xl font-black" style={{ color: 'var(--charity-primary-contrast)' }}>
                      {String(item.value).padStart(2, '0')}
                    </div>
                  </div>
                  <div className="text-[10px] mt-1 uppercase tracking-wider" style={{ color: 'var(--charity-primary-contrast)', opacity: 0.7 }}>
                    <InlineTranslationEdit translationKey={item.tKey} value={c(item.tKey, t?.(item.tKey))} fallback={item.label} onUpdate={handleTranslationUpdate} editMode={editMode} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Corner stars */}
      <Star className="absolute -top-3 -left-3 w-8 h-8 drop-shadow-lg" fill="currentColor" style={{ color: 'var(--charity-primary)' }} />
      <Star className="absolute -top-3 -right-3 w-8 h-8 drop-shadow-lg" fill="currentColor" style={{ color: 'var(--charity-primary)' }} />
      <Star className="absolute -bottom-3 -left-3 w-8 h-8 drop-shadow-lg" fill="currentColor" style={{ color: 'var(--charity-primary)' }} />
      <Star className="absolute -bottom-3 -right-3 w-8 h-8 drop-shadow-lg" fill="currentColor" style={{ color: 'var(--charity-primary)' }} />
    </div>
  )
}

/**
 * Style5 live_draw — Theater-themed 2-col with TheaterJackpotCard + 50/50 split.
 * Mirrors Classic5050Style5.jsx lines 383-468.
 */
export function Style5LiveDrawSection({ mainDraw, loading, t, editMode, handleTranslationUpdate, onLoginClick }) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const ticketsSold = mainDraw?.total_tickets_sold
  const winnerHalf = Math.floor(jackpotAmount / 2)

  useEffect(() => {
    if (!mainDraw?.draw_date) return
    const calc = () => {
      const dist = new Date(mainDraw.draw_date).getTime() - Date.now()
      if (dist < 0) { setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 }); return }
      setTimeRemaining({
        days: Math.floor(dist / 86400000),
        hours: Math.floor((dist % 86400000) / 3600000),
        minutes: Math.floor((dist % 3600000) / 60000),
        seconds: Math.floor((dist % 60000) / 1000),
      })
    }
    calc()
    const interval = setInterval(calc, 1000)
    return () => clearInterval(interval)
  }, [mainDraw?.draw_date])

  if (loading || !mainDraw) return null

  return (
    <div className="pb-16 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-start">
          {/* Left column: Theater Jackpot Card */}
          <div className="space-y-6">
            <TheaterJackpotCard
              jackpot={jackpotAmount}
              ticketsSold={ticketsSold}
              timeRemaining={timeRemaining}
              t={t}
              editMode={editMode}
              handleTranslationUpdate={handleTranslationUpdate}
              c={c}
            />
            <button
              onClick={() => purchaseNavigate('/purchase')}
              className="w-full group relative px-8 py-5 text-xl font-bold rounded-2xl overflow-hidden transition-all hover:scale-[1.02]"
              style={{
                backgroundColor: 'var(--charity-primary)',
                color: 'var(--charity-primary-contrast)',
                boxShadow: '0 0 30px color-mix(in srgb, var(--charity-primary) 30%, transparent)',
              }}
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                <Ticket className="w-6 h-6" />
                {c('classic5050.getTicketsNow', t?.('classic5050.getTicketsNow', 'Get Your Tickets'))}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{
                background: `linear-gradient(to right, color-mix(in srgb, var(--charity-primary) 90%, white), var(--charity-primary), color-mix(in srgb, var(--charity-primary) 90%, white))`,
              }} />
            </button>
          </div>

          {/* Right column: 50/50 Split */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
                <InlineTranslationEdit translationKey="classic5050.howSplitWorks" value={c('classic5050.howSplitWorks', t?.('classic5050.howSplitWorks'))} fallback="How the 50/50 Split Works" onUpdate={handleTranslationUpdate} editMode={editMode} />
              </h3>
              <div className="space-y-4">
                <div className="relative bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-700/50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">
                        <InlineTranslationEdit translationKey="classic5050.winnerGets" value={c('classic5050.winnerGets', t?.('classic5050.winnerGets'))} fallback="Winner Gets" onUpdate={handleTranslationUpdate} editMode={editMode} />
                      </p>
                      <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">${winnerHalf.toLocaleString()}</p>
                    </div>
                    <span className="text-3xl font-black text-emerald-300 dark:text-emerald-700">50%</span>
                  </div>
                </div>
                <div className="relative bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700/50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                        <InlineTranslationEdit translationKey="classic5050.charityGets" value={c('classic5050.charityGets', t?.('classic5050.charityGets'))} fallback="Charity Gets" onUpdate={handleTranslationUpdate} editMode={editMode} />
                      </p>
                      <p className="text-2xl font-black text-blue-600 dark:text-blue-400">${(jackpotAmount - winnerHalf).toLocaleString()}</p>
                    </div>
                    <span className="text-3xl font-black text-blue-300 dark:text-blue-700">50%</span>
                  </div>
                </div>
                <div className="h-4 rounded-full overflow-hidden flex">
                  <div className="w-1/2 bg-gradient-to-r from-emerald-400 to-emerald-500" />
                  <div className="w-1/2 bg-gradient-to-r from-blue-400 to-blue-500" />
                </div>
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  <InlineTranslationEdit translationKey="classic5050.splitExplainer" value={c('classic5050.splitExplainer', t?.('classic5050.splitExplainer'))} fallback="Every ticket sold grows both halves equally!" onUpdate={handleTranslationUpdate} editMode={editMode} />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Style5 how_it_works — 4-step grid with emoji icons.
 * Mirrors Classic5050Style5.jsx lines 472-544.
 */
export function Style5HowItWorksSection({ t, editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  const steps = [
    { emoji: '🎟️', titleKey: 'classic5050.step1Title', descKey: 'classic5050.step1Desc', titleFallback: 'Buy Tickets', descFallback: 'Purchase your tickets for the weekly draw' },
    { emoji: '📈', titleKey: 'classic5050.step2Title', descKey: 'classic5050.step2Desc', titleFallback: 'Jackpot Grows', descFallback: 'Every ticket sold increases the prize pool' },
    { emoji: '🎯', titleKey: 'classic5050.step3Title', descKey: 'classic5050.step3Desc', titleFallback: 'Draw Day', descFallback: 'One lucky ticket is drawn each week' },
    { emoji: '🏆', titleKey: 'classic5050.step4Title', descKey: 'classic5050.step4Desc', titleFallback: '50/50 Split', descFallback: 'Winner takes half, charity gets the other half!' },
  ]

  return (
    <div className="py-16 bg-white dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 dark:text-white mb-10">
          <InlineTranslationEdit translationKey="classic5050.howItWorksTitle" value={c('classic5050.howItWorksTitle', t?.('classic5050.howItWorksTitle'))} fallback="How the 50/50 Works" onUpdate={handleTranslationUpdate} editMode={editMode} />
        </h2>
        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div key={i} className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-3xl" style={{
                background: `linear-gradient(135deg, color-mix(in srgb, var(--charity-primary) 15%, white), color-mix(in srgb, var(--charity-primary) 20%, white))`,
              }}>
                {step.emoji}
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                <InlineTranslationEdit translationKey={step.titleKey} value={c(step.titleKey, t?.(step.titleKey))} fallback={step.titleFallback} onUpdate={handleTranslationUpdate} editMode={editMode} />
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <InlineTranslationEdit translationKey={step.descKey} value={c(step.descKey, t?.(step.descKey))} fallback={step.descFallback} onUpdate={handleTranslationUpdate} editMode={editMode} />
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Style5 final CTA — theater-themed dark CTA with trophy emoji.
 * Mirrors Classic5050Style5.jsx lines 612-639.
 */
export function Style5FinalCTASection({ mainDraw, t, editMode, handleTranslationUpdate, onLoginClick }) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const ticketsSold = mainDraw?.total_tickets_sold

  return (
    <div className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-black dark:via-gray-900 dark:to-black border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <div className="text-6xl mb-6">🏆</div>
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white mb-4">
          <InlineTranslationEdit translationKey="classic5050.finalCtaTitle" value={c('classic5050.finalCtaTitle', t?.('classic5050.finalCtaTitle'))} fallback="Ready to Win?" onUpdate={handleTranslationUpdate} editMode={editMode} />
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
          {ticketsSold != null
            ? c('classic5050.finalTicketsInPot', t?.('classic5050.finalTicketsInPot', 'Only {count} tickets in the pot. More tickets = bigger prizes!'))
              .replace('{count}', ticketsSold.toLocaleString())
            : <InlineTranslationEdit translationKey="classic5050.finalCtaDesc" value={c('classic5050.finalCtaDesc', t?.('classic5050.finalCtaDesc'))} fallback="Get your tickets now for a chance to win half the jackpot!" onUpdate={handleTranslationUpdate} editMode={editMode} />
          }
        </p>
        <button
          onClick={() => purchaseNavigate('/purchase')}
          className="group inline-flex items-center gap-3 px-10 py-5 text-xl font-bold rounded-full transition-all hover:scale-105"
          style={{
            backgroundColor: 'var(--charity-primary)',
            color: 'var(--charity-primary-contrast)',
            boxShadow: '0 0 40px color-mix(in srgb, var(--charity-primary) 30%, transparent)',
          }}
        >
          <span>
            <InlineTranslationEdit translationKey="classic5050.enterDraw" value={c('classic5050.enterDraw', t?.('classic5050.enterDraw'))} fallback="Enter the Draw" onUpdate={handleTranslationUpdate} editMode={editMode} />
          </span>
          <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  )
}
